import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { projectApi } from '../hooks/useApi';
import { useTheme } from '../hooks/useTheme';
import { useTranslation } from 'react-i18next';
import { LanguageSelector } from '../components/LanguageSelector';
import { C, Card, HelpBox, ThemeToggle, DataTable, Td, TdActions, Badge, Tag, Btn, Modal, ModalFooter, FormGrid, FG, Label, Input, Textarea, Select, GraviteSelect, VraisSelect, Empty, RiskItem } from '../components/UI';
import { G_COLOR, V_COLOR, riskLabel, riskColor, uid, PERTINENCE_COLOR, STATUT_COLOR, buildPDFHtml } from '../components/ebios';

const PANELS = ['dashboard','atelier1','atelier2','atelier3','atelier4','atelier5','matrix'];

export default function ProjectPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { theme, toggle } = useTheme();
  const { t } = useTranslation();
  const [p, setP]             = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [panel, setPanel]     = useState('dashboard');
  const timer = useRef(null);

  const NAV = [
    ['dashboard','00', t('nav.dashboard'), null],
    ['atelier1', '01', t('nav.atelier1'),  'er'],
    ['atelier2', '02', t('nav.atelier2'),  'sr'],
    ['atelier3', '03', t('nav.atelier3'),  'ss'],
    ['atelier4', '04', t('nav.atelier4'),  'so'],
    ['atelier5', '05', t('nav.atelier5'),  'mesures'],
    ['matrix',   '▦',  t('nav.matrix'),    null],
  ];

  useEffect(() => {
    projectApi.get(id).then(data => { setP(data); setLoading(false); }).catch(() => navigate('/'));
  }, [id]);

  const save = useCallback((updated) => {
    if (timer.current) clearTimeout(timer.current);
    setSaving(true);
    timer.current = setTimeout(async () => {
      try { await projectApi.update(id, updated); }
      catch (e) { console.error(e); }
      finally { setSaving(false); }
    }, 1200);
  }, [id]);

  function update(field, value) {
    const next = { ...p, [field]: value };
    setP(next); save(next);
  }

  async function share() {
    try {
      const { shareToken } = await projectApi.share(id);
      const url = `${window.location.origin}/shared/${shareToken}`;
      alert(t('project.shareMsg', { url }));
    } catch { alert(t('common.error')); }
  }

  function pdf() {
    const w = window.open('', '_blank');
    w.document.write(buildPDFHtml(p));
    w.document.close();
    setTimeout(() => w.print(), 800);
  }

  if (loading) return <div style={{ minHeight:'100vh', background:C.bg, display:'flex', alignItems:'center', justifyContent:'center', color:C.text2 }}>{t('common.loading')}</div>;
  if (!p) return null;

  const badgeCount = (key) => {
    if (key === 'er') return (p.er?.length||0) + (p.socle?.length||0);
    return p[key]?.length || 0;
  };

  return (
    <div style={{ display:'flex', minHeight:'100vh', background:'var(--bg)', color:'var(--text)', fontFamily:'var(--font-sans)' }}>

      {/* SIDEBAR */}
      <aside style={{ width:248, flexShrink:0, background:'var(--bg-subtle)', borderRight:'1px solid var(--border)', display:'flex', flexDirection:'column', position:'sticky', top:0, height:'100vh', overflowY:'auto' }}>

        {/* Logo */}
        <div style={{ padding:'0 16px', height:54, borderBottom:'1px solid var(--border)', display:'flex', alignItems:'center', gap:10, flexShrink:0 }}>
          <div style={{ width:28, height:28, background:'var(--blue)', borderRadius:6, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, boxShadow:'0 0 0 3px var(--blue-bg)' }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" width="14" height="14"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
          </div>
          <div style={{ minWidth:0 }}>
            <div style={{ fontFamily:'var(--font-display)', fontSize:13.5, fontWeight:700, letterSpacing:-0.4, color:'var(--text)', lineHeight:1.1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{t('app.name')}</div>
            <div style={{ fontSize:9.5, color:'var(--text3)', letterSpacing:1.5, textTransform:'uppercase', marginTop:1 }}>{t('app.tagline')}</div>
          </div>
        </div>

        {/* Project name */}
        <div style={{ padding:'12px 16px 10px', borderBottom:'1px solid var(--border)', flexShrink:0 }}>
          <div style={{ fontSize:10, color:'var(--text3)', textTransform:'uppercase', letterSpacing:1.2, marginBottom:4, fontWeight:600 }}>Projet</div>
          <div style={{ fontSize:13, fontWeight:600, color:'var(--text)', letterSpacing:-0.2, lineHeight:1.3, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{p.name}</div>
          {p.org && <div style={{ fontSize:11.5, color:'var(--text3)', marginTop:2 }}>{p.org}</div>}
        </div>

        {/* Navigation */}
        <nav style={{ padding:'8px 10px', flex:1 }}>
          {NAV.map(([key, icon, label, dataKey]) => {
            const isActive = panel === key;
            const isNum = /^\d/.test(icon);
            return (
              <div key={key} onClick={() => setPanel(key)}
                style={{ display:'flex', alignItems:'center', gap:9, padding:'8px 9px', borderRadius:6, cursor:'pointer', fontSize:12.5, fontWeight:isActive?600:400, color:isActive?'var(--text)':'var(--text2)', background:isActive?'var(--bg-muted)':'transparent', borderLeft:`2px solid ${isActive?'var(--blue)':'transparent'}`, paddingLeft:isActive?7:9, marginBottom:2, userSelect:'none', transition:'all .1s' }}>
                <span style={{ fontFamily:'var(--font-mono)', fontSize:isNum?10:13, fontWeight:isNum?600:400, color:isActive?'var(--blue)':'var(--text4)', width:24, textAlign:'center', flexShrink:0 }}>{icon}</span>
                <span style={{ flex:1 }}>{label}</span>
                {dataKey && (
                  <span style={{ background:isActive?'var(--blue)':'var(--bg-muted)', color:isActive?'white':'var(--text3)', fontSize:10.5, padding:'1px 6px', borderRadius:4, fontFamily:'var(--font-mono)', border:isActive?'none':'1px solid var(--border)', marginLeft:'auto', fontWeight:600, minWidth:22, textAlign:'center' }}>
                    {badgeCount(dataKey)}
                  </span>
                )}
              </div>
            );
          })}
        </nav>

        {/* Progress */}
        <div style={{ padding:'12px 16px', borderTop:'1px solid var(--border)', flexShrink:0 }}>
          <div style={{ fontSize:10.5, color:'var(--text3)', marginBottom:8, fontWeight:600, textTransform:'uppercase', letterSpacing:0.8 }}>{t('project.progression')}</div>
          <div style={{ display:'flex', gap:3 }}>
            {[p.er?.length>0, p.sr?.length>0, p.ss?.length>0, p.so?.length>0, p.mesures?.length>0].map((done,i) => (
              <div key={i} style={{ flex:1, height:3, borderRadius:2, background:done?'var(--blue)':'var(--border)', transition:'background .3s' }} />
            ))}
          </div>
        </div>
      </aside>

      {/* MAIN */}
      <div style={{ flex:1, display:'flex', flexDirection:'column', minWidth:0 }}>

        {/* Topbar */}
        <div style={{ padding:'0 20px', height:54, borderBottom:'1px solid var(--border)', background:'var(--bg)', display:'flex', alignItems:'center', justifyContent:'space-between', position:'sticky', top:0, zIndex:50 }}>
          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
            <button onClick={() => navigate('/')}
              style={{ display:'flex', alignItems:'center', gap:4, padding:'5px 8px', borderRadius:5, border:'1px solid transparent', background:'transparent', color:'var(--text3)', cursor:'pointer', fontSize:12, transition:'all .1s' }}
              onMouseEnter={e => { e.currentTarget.style.background='var(--bg-muted)'; e.currentTarget.style.borderColor='var(--border)'; e.currentTarget.style.color='var(--text2)'; }}
              onMouseLeave={e => { e.currentTarget.style.background='transparent'; e.currentTarget.style.borderColor='transparent'; e.currentTarget.style.color='var(--text3)'; }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" width="13" height="13"><polyline points="15 18 9 12 15 6"/></svg>
              {t('nav.myProjects')}
            </button>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" width="12" height="12" style={{color:'var(--text4)'}}><polyline points="9 18 15 12 9 6"/></svg>
            <div style={{ fontSize:13.5, fontWeight:600, fontFamily:'var(--font-display)', letterSpacing:-0.2 }}>{p.name}</div>
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
            <span style={{ fontSize:11.5, color:'var(--text3)' }}>{saving ? t('project.saving') : t('project.saved')}</span>
            <LanguageSelector />
            <ThemeToggle theme={theme} onToggle={toggle} />
            <Btn variant="gold" onClick={pdf} style={{ height:32, padding:'0 12px', fontSize:12.5 }}>{t('project.pdf')}</Btn>
            <Btn variant="secondary" onClick={share} style={{ height:32, padding:'0 12px', fontSize:12.5 }}>{t('project.share')}</Btn>
          </div>
        </div>

        <div style={{ padding:28, flex:1, overflowY:'auto' }}>
          {panel === 'dashboard' && <Dashboard p={p} setPanel={setPanel} t={t} />}
          {panel === 'atelier1'  && <Atelier1 p={p} update={update} t={t} />}
          {panel === 'atelier2'  && <Atelier2 p={p} update={update} t={t} />}
          {panel === 'atelier3'  && <Atelier3 p={p} update={update} t={t} />}
          {panel === 'atelier4'  && <Atelier4 p={p} update={update} t={t} />}
          {panel === 'atelier5'  && <Atelier5 p={p} update={update} t={t} />}
          {panel === 'matrix'    && <Matrix   p={p} t={t} />}
        </div>
      </div>
    </div>
  );
}

// ── DASHBOARD ──────────────────────────────────────────────────────────────
function Dashboard({ p, setPanel, t }) {
  const high = (p.so||[]).filter(s => s.gravite*s.vrais >= 6).length;
  const sorted = [...(p.so||[])].sort((a,b) => (b.gravite*b.vrais)-(a.gravite*a.vrais));
  return (
    <div>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:16, marginBottom:24 }}>
        {[['📋', p.er?.length||0, t('atelier1.er.title'),    C.accent,  'atelier1'],
          ['🕵️', p.sr?.length||0, t('atelier2.title').split('—')[0].trim(), '#7c5cff','atelier2'],
          ['🗺️', p.ss?.length||0, t('atelier3.title').split('—')[0].trim(), C.warn,   'atelier3'],
          ['⚠️', high,            t('risk.high')+'/'+t('risk.critical'), C.danger, 'matrix'],
        ].map(([icon,val,lbl,col,nav]) => (
          <div key={nav} onClick={() => setPanel(nav)} style={{ background:C.surface, border:`1px solid ${C.border}`, borderTop:`2px solid ${col}`, borderRadius:12, padding:20, cursor:'pointer' }}>
            <div style={{ fontSize:24, marginBottom:8 }}>{icon}</div>
            <div style={{ fontFamily:'Georgia,serif', fontSize:32, lineHeight:1 }}>{val}</div>
            <div style={{ fontSize:11, color:C.text3, marginTop:6, textTransform:'uppercase', letterSpacing:1 }}>{lbl}</div>
          </div>
        ))}
      </div>
      <Card title={t('matrix.title')} subtitle={t('matrix.subtitle')}>
        {sorted.length === 0
          ? <Empty icon="📊" text={t('atelier4.empty')} sub={t('matrix.emptySub')} />
          : sorted.map(s => {
              const sc = s.gravite*s.vrais;
              const ss = (p.ss||[]).find(x=>x.id===s.ssId);
              return <RiskItem key={s.id}>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:14, fontWeight:600, color:C.text }}>{s.nom}</div>
                  <div style={{ fontSize:12, color:C.text3, marginTop:3 }}>{ss?.nom}</div>
                </div>
                <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                  <Badge color={G_COLOR[s.gravite]}>{t(`gravite.${s.gravite}`)}</Badge>
                  <Badge color={V_COLOR[s.vrais]}>{t(`vraisemblance.${s.vrais}`)}</Badge>
                  <Badge color={riskColor(sc)}>{t('risk.score', { score: sc, level: riskLabel(sc) })}</Badge>
                </div>
              </RiskItem>;
            })
        }
      </Card>
    </div>
  );
}

// ── ATELIER 1 ──────────────────────────────────────────────────────────────
function Atelier1({ p, update, t }) {
  const [modal, setModal] = useState(null);
  function saveER(item) {
    const list = item.id && (p.er||[]).find(x=>x.id===item.id) ? (p.er||[]).map(x=>x.id===item.id?item:x) : [...(p.er||[]), {...item, id:uid()}];
    update('er', list); setModal(null);
  }
  function saveSocle(item) {
    const list = item.id && (p.socle||[]).find(x=>x.id===item.id) ? (p.socle||[]).map(x=>x.id===item.id?item:x) : [...(p.socle||[]), {...item, id:uid()}];
    update('socle', list); setModal(null);
  }
  const ETAT_COLOR = {
    'Non appliqué':'var(--red)','Partiellement appliqué':'var(--amber)','Appliqué':'var(--blue)','Certifié':'var(--green)',
    'Not applied':'var(--red)','Partially applied':'var(--amber)','Applied':'var(--blue)','Certified':'var(--green)',
    'Nicht angewendet':'var(--red)','Teilweise angewendet':'var(--amber)','Angewendet':'var(--blue)','Zertifiziert':'var(--green)',
    'Non applicato':'var(--red)','Parzialmente applicato':'var(--amber)','Applicato':'var(--blue)','Certificato':'var(--green)'
  };
  return (
    <div>
      <HelpBox title={t('atelier1.title')}>{t('atelier1.desc')}</HelpBox>
      <Card title={t('atelier1.er.title')} action={<Btn onClick={() => setModal({type:'er',data:{}})}>{t('atelier1.er.add')}</Btn>}>
        <DataTable cols={[...t('atelier1.er.cols',{returnObjects:true}),'']} empty={<Empty icon="🎯" text={t('atelier1.er.empty')} />}>
          {(p.er||[]).map(e => <tr key={e.id}>
            <Td bold>{e.vm}</Td><Td>{e.name}</Td><Td small>{e.impacts}</Td>
            <Td><Badge color={G_COLOR[e.gravite]}>{t(`gravite.${e.gravite}`)}</Badge></Td>
            <TdActions onEdit={() => setModal({type:'er',data:e})} onDel={() => update('er',(p.er||[]).filter(x=>x.id!==e.id))} />
          </tr>)}
        </DataTable>
      </Card>
      <Card title={t('atelier1.socle.title')} action={<Btn onClick={() => setModal({type:'socle',data:{}})}>{t('atelier1.socle.add')}</Btn>}>
        <DataTable cols={[...t('atelier1.socle.cols',{returnObjects:true}),'']} empty={<Empty icon="🛡️" text={t('atelier1.socle.empty')} />}>
          {(p.socle||[]).map(s => <tr key={s.id}>
            <Td bold>{s.nom} <Tag>{s.type}</Tag></Td>
            <Td><Badge color={ETAT_COLOR[s.etat]||'var(--text3)'}>{s.etat}</Badge></Td>
            <Td small>{s.ecarts}</Td><Td small>{s.just}</Td>
            <TdActions onEdit={() => setModal({type:'socle',data:s})} onDel={() => update('socle',(p.socle||[]).filter(x=>x.id!==s.id))} />
          </tr>)}
        </DataTable>
      </Card>
      {modal?.type==='er' && <Modal title={modal.data?.id ? t('atelier1.er.editTitle') : t('atelier1.er.addTitle')} onClose={() => setModal(null)}><ERForm d={modal.data} onSave={saveER} onCancel={() => setModal(null)} t={t} /></Modal>}
      {modal?.type==='socle' && <Modal title={t('atelier1.socle.title')} onClose={() => setModal(null)}><SocleForm d={modal.data} onSave={saveSocle} onCancel={() => setModal(null)} t={t} /></Modal>}
    </div>
  );
}
function ERForm({ d: init, onSave, onCancel, t }) {
  const [d, setD] = useState({ vm:'', name:'', impacts:'', gravite:3, ...init });
  return <FormGrid>
    <FG full><Label>{t('atelier1.er.vm')}</Label><Input value={d.vm} onChange={e=>setD({...d,vm:e.target.value})} placeholder={t('atelier1.er.vmPlaceholder')} /></FG>
    <FG full><Label>{t('atelier1.er.name')}</Label><Input value={d.name} onChange={e=>setD({...d,name:e.target.value})} placeholder={t('atelier1.er.namePlaceholder')} /></FG>
    <FG full><Label>{t('atelier1.er.impacts')}</Label><Textarea value={d.impacts} onChange={e=>setD({...d,impacts:e.target.value})} rows={4} /></FG>
    <FG><Label>{t('atelier1.er.gravite')}</Label><GraviteSelect value={d.gravite} onChange={e=>setD({...d,gravite:+e.target.value})} t={t} /></FG>
    <ModalFooter onCancel={onCancel} onSave={() => { if(!d.vm||!d.name){alert(t('common.required'));return;} onSave(d); }} />
  </FormGrid>;
}
function SocleForm({ d: init, onSave, onCancel, t }) {
  const types  = t('atelier1.socle.types',  {returnObjects:true});
  const states = t('atelier1.socle.states', {returnObjects:true});
  const [d, setD] = useState({ type: types[0], nom:'', etat: states[0], ecarts:'', just:'', ...init });
  return <FormGrid>
    <FG><Label>{t('atelier1.socle.type')}</Label><Select value={d.type} onChange={e=>setD({...d,type:e.target.value})}>{types.map(t=><option key={t}>{t}</option>)}</Select></FG>
    <FG><Label>{t('atelier1.socle.name')}</Label><Input value={d.nom} onChange={e=>setD({...d,nom:e.target.value})} placeholder={t('atelier1.socle.namePlaceholder')} /></FG>
    <FG><Label>{t('atelier1.socle.state')}</Label><Select value={d.etat} onChange={e=>setD({...d,etat:e.target.value})}>{states.map(s=><option key={s}>{s}</option>)}</Select></FG>
    <FG full><Label>{t('atelier1.socle.gaps')}</Label><Textarea value={d.ecarts} onChange={e=>setD({...d,ecarts:e.target.value})} rows={3} /></FG>
    <FG full><Label>{t('atelier1.socle.justification')}</Label><Textarea value={d.just} onChange={e=>setD({...d,just:e.target.value})} rows={3} /></FG>
    <ModalFooter onCancel={onCancel} onSave={() => { if(!d.nom){alert(t('common.required'));return;} onSave(d); }} />
  </FormGrid>;
}

// ── ATELIER 2 ──────────────────────────────────────────────────────────────
function Atelier2({ p, update, t }) {
  const [modal, setModal] = useState(null);
  function saveSR(item) {
    const list = item.id && (p.sr||[]).find(x=>x.id===item.id) ? (p.sr||[]).map(x=>x.id===item.id?item:x) : [...(p.sr||[]), {...item, id:uid()}];
    update('sr', list); setModal(null);
  }
  return (
    <div>
      <HelpBox title={t('atelier2.title')}>{t('atelier2.desc')}</HelpBox>
      <Card title={t('atelier2.title').split('—')[1]?.trim()||'SR/OV'} action={<Btn onClick={() => setModal({type:'sr',data:{}})}>{t('atelier2.add')}</Btn>}>
        <DataTable cols={[...t('atelier2.cols',{returnObjects:true}),'']} empty={<Empty icon="🕵️" text={t('atelier2.empty')} />}>
          {(p.sr||[]).map(s => <tr key={s.id}>
            <Td bold>{s.type}{s.nom&&<><br/><small style={{color:C.text3}}>{s.nom}</small></>}</Td>
            <Td>{s.ov}</Td><Td>{s.motiv}</Td><Td>{s.res}</Td>
            <Td><Badge color={PERTINENCE_COLOR[s.pert]||'var(--text3)'}>{s.pert}</Badge></Td>
            <TdActions onEdit={() => setModal({type:'sr',data:s})} onDel={() => update('sr',(p.sr||[]).filter(x=>x.id!==s.id))} />
          </tr>)}
        </DataTable>
      </Card>
      {modal?.type==='sr' && <Modal title={t('atelier2.modalTitle')} onClose={() => setModal(null)}><SRForm d={modal.data} onSave={saveSR} onCancel={() => setModal(null)} t={t} /></Modal>}
    </div>
  );
}
function SRForm({ d: init, onSave, onCancel, t }) {
  const srTypes = Object.values(t('sr_types', {returnObjects:true}));
  const motivs  = Object.values(t('motivations', {returnObjects:true}));
  const ress    = ['Limitées','Moyennes','Importantes','Très importantes'];
  const perts   = Object.values(t('pertinences', {returnObjects:true}));
  const [d, setD] = useState({ type: srTypes[0], nom:'', ov:'', motiv: motivs[0], res: ress[0], pert: perts[1], ...init });
  return <FormGrid>
    <FG full><Label>{t('atelier2.category')}</Label><Select value={d.type} onChange={e=>setD({...d,type:e.target.value})}>{srTypes.map(t=><option key={t}>{t}</option>)}</Select></FG>
    <FG full><Label>{t('atelier2.precision')}</Label><Input value={d.nom} onChange={e=>setD({...d,nom:e.target.value})} /></FG>
    <FG full><Label>{t('atelier2.objective')}</Label><Input value={d.ov} onChange={e=>setD({...d,ov:e.target.value})} /></FG>
    <FG><Label>{t('atelier2.motivation')}</Label><Select value={d.motiv} onChange={e=>setD({...d,motiv:e.target.value})}>{motivs.map(m=><option key={m}>{m}</option>)}</Select></FG>
    <FG><Label>{t('atelier2.resources')}</Label><Select value={d.res} onChange={e=>setD({...d,res:e.target.value})}>{ress.map(r=><option key={r}>{r}</option>)}</Select></FG>
    <FG><Label>{t('atelier2.relevance')}</Label><Select value={d.pert} onChange={e=>setD({...d,pert:e.target.value})}>{perts.map(p=><option key={p}>{p}</option>)}</Select></FG>
    <ModalFooter onCancel={onCancel} onSave={() => { if(!d.ov){alert(t('common.required'));return;} onSave(d); }} />
  </FormGrid>;
}

// ── ATELIER 3 ──────────────────────────────────────────────────────────────
function Atelier3({ p, update, t }) {
  const [modal, setModal] = useState(null);
  function saveSS(item) {
    const list = item.id && (p.ss||[]).find(x=>x.id===item.id) ? (p.ss||[]).map(x=>x.id===item.id?item:x) : [...(p.ss||[]), {...item, id:uid()}];
    update('ss', list); setModal(null);
  }
  return (
    <div>
      <HelpBox title={t('atelier3.title')}>{t('atelier3.desc')}</HelpBox>
      <Card title={t('atelier3.title').split('—')[1]?.trim()||'SS'} action={<Btn onClick={() => setModal({type:'ss',data:{}})}>{t('atelier3.add')}</Btn>}>
        <DataTable cols={[...t('atelier3.cols',{returnObjects:true}),'']} empty={<Empty icon="🗺️" text={t('atelier3.empty')} />}>
          {(p.ss||[]).map(s => {
            const sr = (p.sr||[]).find(x=>x.id===s.srId);
            const erTags = (s.erIds||[]).map(eid => { const e=(p.er||[]).find(x=>x.id===eid); return e?<Tag key={eid}>{e.name}</Tag>:null; });
            return <tr key={s.id}>
              <Td bold>{s.nom}</Td><Td>{sr?.type||'—'}</Td>
              <Td>{erTags.length?erTags:'—'}</Td>
              <Td small>{s.chemin}</Td>
              <Td><Badge color={G_COLOR[s.gravite]}>{t(`gravite.${s.gravite}`)}</Badge></Td>
              <TdActions onEdit={() => setModal({type:'ss',data:s})} onDel={() => update('ss',(p.ss||[]).filter(x=>x.id!==s.id))} />
            </tr>;
          })}
        </DataTable>
      </Card>
      {modal?.type==='ss' && <Modal title={t('atelier3.modalTitle')} onClose={() => setModal(null)} wide><SSForm d={modal.data} sr={p.sr||[]} er={p.er||[]} onSave={saveSS} onCancel={() => setModal(null)} t={t} /></Modal>}
    </div>
  );
}
function SSForm({ d: init, sr, er, onSave, onCancel, t }) {
  const [d, setD] = useState({ nom:'', srId:'', erIds:[], chemin:'', gravite:3, ...init });
  return <FormGrid>
    <FG full><Label>{t('atelier3.name')}</Label><Input value={d.nom} onChange={e=>setD({...d,nom:e.target.value})} placeholder={t('atelier3.namePlaceholder')} /></FG>
    <FG><Label>{t('atelier3.source')}</Label>
      <Select value={d.srId} onChange={e=>setD({...d,srId:e.target.value})}>
        <option value="">{t('atelier3.select')}</option>
        {sr.map(s=><option key={s.id} value={s.id}>{s.type}{s.nom?' — '+s.nom:''}</option>)}
      </Select>
    </FG>
    <FG><Label>{t('atelier3.gravite')}</Label><GraviteSelect value={d.gravite} onChange={e=>setD({...d,gravite:+e.target.value})} t={t} /></FG>
    <FG full>
      <Label>{t('atelier3.erLinked')}</Label>
      <select style={{ background:'var(--input-bg)', border:`1px solid ${C.border}`, color:C.text, padding:'10px 14px', borderRadius:8, fontSize:14, outline:'none', width:'100%', minHeight:100 }}
        multiple value={d.erIds} onChange={e=>setD({...d,erIds:Array.from(e.target.selectedOptions).map(o=>o.value)})}>
        {er.map(e=><option key={e.id} value={e.id}>{e.name}</option>)}
      </select>
    </FG>
    <FG full><Label>{t('atelier3.path')}</Label><Textarea value={d.chemin} onChange={e=>setD({...d,chemin:e.target.value})} rows={5} placeholder={t('atelier3.pathPlaceholder')} /></FG>
    <ModalFooter onCancel={onCancel} onSave={() => { if(!d.nom){alert(t('common.required'));return;} onSave(d); }} />
  </FormGrid>;
}

// ── ATELIER 4 ──────────────────────────────────────────────────────────────
function Atelier4({ p, update, t }) {
  const [modal, setModal] = useState(null);
  function saveSO(item) {
    const ssItem = (p.ss||[]).find(x=>x.id===item.ssId);
    const full = { ...item, gravite: ssItem?.gravite || 2 };
    const list = full.id && (p.so||[]).find(x=>x.id===full.id) ? (p.so||[]).map(x=>x.id===full.id?full:x) : [...(p.so||[]), {...full, id:uid()}];
    update('so', list); setModal(null);
  }
  return (
    <div>
      <HelpBox title={t('atelier4.title')}>{t('atelier4.desc')}</HelpBox>
      <Card title={t('atelier4.title').split('—')[1]?.trim()||'SO'} action={<Btn onClick={() => setModal({type:'so',data:{}})}>{t('atelier4.add')}</Btn>}>
        <DataTable cols={[...t('atelier4.cols',{returnObjects:true}),'']} empty={<Empty icon="⚙️" text={t('atelier4.empty')} />}>
          {(p.so||[]).map(s => {
            const ss = (p.ss||[]).find(x=>x.id===s.ssId);
            const sc = s.gravite*s.vrais;
            return <tr key={s.id}>
              <Td bold>{s.nom}</Td><Td>{ss?.nom||'—'}</Td><Td small>{s.mode}</Td>
              <Td><Badge color={V_COLOR[s.vrais]}>{t(`vraisemblance.${s.vrais}`)}</Badge></Td>
              <Td><Badge color={riskColor(sc)}>{riskLabel(sc)} ({s.gravite}×{s.vrais}={sc})</Badge></Td>
              <TdActions onEdit={() => setModal({type:'so',data:s})} onDel={() => update('so',(p.so||[]).filter(x=>x.id!==s.id))} />
            </tr>;
          })}
        </DataTable>
      </Card>
      {modal?.type==='so' && <Modal title={t('atelier4.modalTitle')} onClose={() => setModal(null)}><SOForm d={modal.data} ss={p.ss||[]} onSave={saveSO} onCancel={() => setModal(null)} t={t} /></Modal>}
    </div>
  );
}
function SOForm({ d: init, ss, onSave, onCancel, t }) {
  const [d, setD] = useState({ nom:'', ssId:'', mode:'', vrais:2, ...init });
  return <FormGrid>
    <FG full><Label>{t('atelier4.name')}</Label><Input value={d.nom} onChange={e=>setD({...d,nom:e.target.value})} placeholder={t('atelier4.namePlaceholder')} /></FG>
    <FG full><Label>{t('atelier4.linked')}</Label>
      <Select value={d.ssId} onChange={e=>setD({...d,ssId:e.target.value})}>
        <option value="">{t('atelier4.none')}</option>
        {ss.map(s=><option key={s.id} value={s.id}>{s.nom}</option>)}
      </Select>
    </FG>
    <FG full><Label>{t('atelier4.mode')}</Label><Textarea value={d.mode} onChange={e=>setD({...d,mode:e.target.value})} rows={5} placeholder={t('atelier4.modePlaceholder')} /></FG>
    <FG><Label>{t('atelier4.vraisemblance')}</Label><VraisSelect value={d.vrais} onChange={e=>setD({...d,vrais:+e.target.value})} t={t} /></FG>
    <ModalFooter onCancel={onCancel} onSave={() => { if(!d.nom){alert(t('common.required'));return;} onSave(d); }} />
  </FormGrid>;
}

// ── ATELIER 5 ──────────────────────────────────────────────────────────────
function Atelier5({ p, update, t }) {
  const [modal, setModal] = useState(null);
  function saveMesure(item) {
    const list = item.id && (p.mesures||[]).find(x=>x.id===item.id) ? (p.mesures||[]).map(x=>x.id===item.id?item:x) : [...(p.mesures||[]), {...item, id:uid()}];
    update('mesures', list); setModal(null);
  }
  return (
    <div>
      <HelpBox title={t('atelier5.title')}>{t('atelier5.desc')}</HelpBox>
      <Card title={t('atelier5.title').split('—')[1]?.trim()||'Mesures'} action={<Btn onClick={() => setModal({type:'m',data:{}})}>{t('atelier5.add')}</Btn>}>
        <DataTable cols={[...t('atelier5.cols',{returnObjects:true}),'']} empty={<Empty icon="🔐" text={t('atelier5.empty')} />}>
          {(p.mesures||[]).map(m => {
            const scTags = (m.sc||[]).map(sid=>{ const s=(p.so||[]).find(x=>x.id===sid); return s?<Tag key={sid}>{s.nom}</Tag>:null; });
            return <tr key={m.id}>
              <Td bold>{m.nom}<br/><small style={{color:C.text3}}>{m.strat}</small></Td>
              <Td>{scTags.length?scTags:'—'}</Td>
              <Td>{m.resp||'—'}</Td><Td>{m.cout}</Td><Td>{m.ech||'—'}</Td>
              <Td><Badge color={STATUT_COLOR[m.stat]||'var(--text3)'}>{m.stat}</Badge></Td>
              <TdActions onEdit={() => setModal({type:'m',data:m})} onDel={() => update('mesures',(p.mesures||[]).filter(x=>x.id!==m.id))} />
            </tr>;
          })}
        </DataTable>
      </Card>
      {modal?.type==='m' && <Modal title={t('atelier5.modalTitle')} onClose={() => setModal(null)} wide><MesureForm d={modal.data} so={p.so||[]} onSave={saveMesure} onCancel={() => setModal(null)} t={t} /></Modal>}
    </div>
  );
}
function MesureForm({ d: init, so, onSave, onCancel, t }) {
  const strats = Object.values(t('strategies', {returnObjects:true}));
  const couts  = Object.values(t('couts',      {returnObjects:true}));
  const statuts= Object.values(t('statuts',    {returnObjects:true}));
  const [d, setD] = useState({ nom:'', strat: strats[0], resp:'', cout: couts[1], ech:'', stat: statuts[0], sc:[], freins:'', ...init });
  return <FormGrid>
    <FG full><Label>{t('atelier5.name')}</Label><Input value={d.nom} onChange={e=>setD({...d,nom:e.target.value})} /></FG>
    <FG><Label>{t('atelier5.strategy')}</Label><Select value={d.strat} onChange={e=>setD({...d,strat:e.target.value})}>{strats.map(s=><option key={s}>{s}</option>)}</Select></FG>
    <FG><Label>{t('atelier5.responsible')}</Label><Input value={d.resp} onChange={e=>setD({...d,resp:e.target.value})} /></FG>
    <FG><Label>{t('atelier5.cost')}</Label><Select value={d.cout} onChange={e=>setD({...d,cout:e.target.value})}>{couts.map(c=><option key={c}>{c}</option>)}</Select></FG>
    <FG><Label>{t('atelier5.deadline')}</Label><Input type="date" value={d.ech} onChange={e=>setD({...d,ech:e.target.value})} /></FG>
    <FG><Label>{t('atelier5.status')}</Label><Select value={d.stat} onChange={e=>setD({...d,stat:e.target.value})}>{statuts.map(s=><option key={s}>{s}</option>)}</Select></FG>
    <FG full>
      <Label>{t('atelier5.scenarios')}</Label>
      <select style={{ background:'var(--input-bg)', border:`1px solid ${C.border}`, color:C.text, padding:'10px 14px', borderRadius:8, fontSize:14, outline:'none', width:'100%', minHeight:90 }}
        multiple value={d.sc} onChange={e=>setD({...d,sc:Array.from(e.target.selectedOptions).map(o=>o.value)})}>
        {so.map(s=><option key={s.id} value={s.id}>{s.nom}</option>)}
      </select>
    </FG>
    <FG full><Label>{t('atelier5.obstacles')}</Label><Textarea value={d.freins} onChange={e=>setD({...d,freins:e.target.value})} rows={3} /></FG>
    <ModalFooter onCancel={onCancel} onSave={() => { if(!d.nom){alert(t('common.required'));return;} onSave(d); }} />
  </FormGrid>;
}

// ── MATRIX ─────────────────────────────────────────────────────────────────
function Matrix({ p, t }) {
  const cells = {};
  (p.so||[]).forEach(s => { const k=`${s.gravite}-${s.vrais}`; if(!cells[k])cells[k]=[]; cells[k].push(s.nom); });
  const sorted = [...(p.so||[])].sort((a,b)=>(b.gravite*b.vrais)-(a.gravite*a.vrais));

  function bg(sc){ return sc>=9?'var(--red-bg)':sc>=6?'var(--orange-bg)':sc>=3?'var(--blue-bg)':sc>=1?'var(--green-bg)':'var(--bg-muted)'; }
  function bdr(sc){ return sc>=9?'var(--red-border)':sc>=6?'var(--orange-border)':sc>=3?'var(--blue-border)':sc>=1?'var(--green-border)':'var(--border)'; }

  const legend = t('matrix.legend', {returnObjects:true});

  return (
    <Card title={t('matrix.title')} subtitle={t('matrix.subtitle')}>
      <div style={{ display:'flex', gap:40, flexWrap:'wrap', alignItems:'flex-start' }}>
        <div>
          <div style={{ fontSize:11, color:C.text3, textTransform:'uppercase', letterSpacing:2, marginBottom:16, fontFamily:'monospace' }}>{t('matrix.axisLabel')}</div>
          <div style={{ display:'grid', gridTemplateColumns:'110px repeat(4,80px)', gap:3 }}>
            {[4,3,2,1].map(g => (
              <React.Fragment key={g}>
                <div style={{ display:'flex', alignItems:'center', justifyContent:'flex-end', paddingRight:10, fontSize:10, color:C.text3, height:64 }}>{t(`gravite.${g}`)}</div>
                {[1,2,3,4].map(v => {
                  const sc=g*v; const list=cells[`${g}-${v}`]||[];
                  return <div key={v} style={{ height:64, borderRadius:8, background:bg(sc), border:`1px solid ${bdr(sc)}`, display:'flex', alignItems:'center', justifyContent:'center', fontFamily:'monospace', fontWeight:700, fontSize:14, color:riskColor(sc), position:'relative', cursor:'default' }} title={list.join('\n')}>
                    {sc}{list.length>0&&<span style={{ position:'absolute', top:3, right:4, fontSize:8 }}>{'●'.repeat(Math.min(list.length,5))}</span>}
                  </div>;
                })}
              </React.Fragment>
            ))}
            <div style={{ height:40 }} />
            {[1,2,3,4].map(v=><div key={v} style={{ height:40, fontSize:9, color:C.text3, textAlign:'center', paddingTop:6, fontFamily:'monospace' }}>{t(`vraisemblance.${v}`).split(' ')[0]}<br/>{t(`vraisemblance.${v}`).split(' ').slice(1).join(' ')}</div>)}
          </div>
          <div style={{ display:'flex', gap:16, flexWrap:'wrap', marginTop:16 }}>
            {[['var(--green)', legend.low],['var(--blue)', legend.medium],['var(--orange)', legend.high],['var(--red)', legend.critical]].map(([c,l])=>(
              <div key={l} style={{ display:'flex', alignItems:'center', gap:6, fontSize:12, color:C.text2 }}>
                <div style={{ width:10, height:10, borderRadius:3, background:c }} />{l}
              </div>
            ))}
          </div>
        </div>
        <div style={{ flex:1, minWidth:280 }}>
          <div style={{ fontSize:11, color:C.text3, textTransform:'uppercase', letterSpacing:2, marginBottom:16, fontFamily:'monospace' }}>{t('matrix.scenarios')}</div>
          {sorted.length===0
            ? <Empty icon="📍" text={t('matrix.empty')} sub={t('matrix.emptySub')} />
            : sorted.map(s => {
                const sc=s.gravite*s.vrais;
                return <RiskItem key={s.id}>
                  <div><div style={{ fontSize:14, fontWeight:600, color:C.text }}>{s.nom}</div><div style={{ fontSize:12, color:C.text3, marginTop:3 }}>G{s.gravite} × V{s.vrais} = {sc}</div></div>
                  <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                    <Badge color={G_COLOR[s.gravite]}>{t(`gravite.${s.gravite}`)}</Badge>
                    <Badge color={V_COLOR[s.vrais]}>{t(`vraisemblance.${s.vrais}`)}</Badge>
                    <Badge color={riskColor(sc)}>{riskLabel(sc)}</Badge>
                  </div>
                </RiskItem>;
              })
          }
        </div>
      </div>
    </Card>
  );
}
